/* eslint-disable camelcase */
/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { CardBody, Col, Input, Row } from 'reactstrap';
import toast from '../../utils/toast';
import CollapsibleCard from '../CollapsibleCard';
import { environment } from '../../environment/environment';

export default function ManageMultipleMatchingCriterias({ employerId, features, title }) {
  const matchingCriteriaDictionary = {
    id_number: 'ID Number',
    employee_no: 'Employee Number',
    bank_account_number: 'Bank Account Number',
    first_name: 'First Name',
    last_name: 'Last Name',
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configuration, setConfiguration] = useState({
    configurations: features.map((feature) => ({
      feature_name: feature.name,
      configuration: null
    })),
    combine: true
  });

  const getFeatureConfiguration = useCallback((feature_name) => {
    axios.get(`${environment.baseUrl}/employer_features_config/feature_configuration/?employer_id=${employerId}&feature_name=${feature_name}`)
      .then((res) => res.data)
      .then((data) => {
        setConfiguration((prev) => ({
          ...prev,
          configurations: prev.configurations.map((config) => {
            if (config.feature_name === feature_name) {
              return {
                ...config,
                configuration: data
              };
            }
            return config;
          })
        }));
        return data;
      })
      .catch(() => {
        setError(`Failed to fetch employer ${feature_name} configuration`);
        return null;
      });
  }, [employerId]);

  const showToast = useCallback((text, icon) => {
    toast[icon](text);
  }, []);

  const updateMatchingCriterias = async () => {
    setLoading(true);
    const errors = [];

    try {
      // Use Promise.allSettled to handle all requests concurrently
      const results = await Promise.allSettled(
        configuration.configurations.map(async (config) => {
          try {
            await axios.put(
              `${environment.baseUrl}/employer_features_config/${config.configuration.id}/update_feature_configuration/`,
              { configuration: config.configuration.configuration }
            );
            return { success: true, feature_name: config.feature_name };
          } catch (err) {
            return {
              success: false,
              feature_name: config.feature_name,
              error: `Failed to update ${config.feature_name} configuration`
            };
          }
        })
      );

      // Collect errors from failed requests
      results.forEach((result) => {
        if (result.status === 'fulfilled' && !result.value.success) {
          errors.push(result.value.error);
        } else if (result.status === 'rejected') {
          errors.push('An unexpected error occurred');
        }
      });

      // Show appropriate message
      if (errors.length === 0) {
        showToast(`${title} matching criteria updated`, 'success');
      } else {
        showToast(errors.join(', '), 'error');
      }
    } catch (err) {
      showToast('An unexpected error occurred while updating configurations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleField = useCallback((field, feature_name) => {

    const currentConfig = configuration.configurations.find((config) => config.feature_name === feature_name);
    let fields = currentConfig.configuration.configuration.matching_criteria.split(',');
    if (fields.includes(field)) {
      fields = fields.filter((f) => f !== field);
    } else {
      fields.push(field);
    }

    if (fields.length === 0) {
      setError('At least one matching criteria must be selected');
      return;
    }

    if (configuration.combine) {
      setConfiguration((prev) => ({
        ...prev,
        configurations: prev.configurations.map((c) => ({
          ...c,
          configuration: {
            ...c.configuration,
            configuration: {
              ...c.configuration.configuration,
              matching_criteria: fields.join(','),
            }
          }
        }))
      }));
    } else {
      setConfiguration((prev) => ({
        ...prev,
        configurations: prev.configurations.map((c) => {
          if (c.feature_name === feature_name) {
            return {
              ...c,
              configuration: {
                ...c.configuration,
                configuration: {
                  ...c.configuration.configuration,
                  matching_criteria: fields.join(','),
                }
              }
            };
          }
          return c;
        })
      }));
    }

  }, [configuration.configurations, configuration.combine]);


  const updateCombinedCriteria = async (checked) => {
    if (!checked) {
      Swal.fire({
        title: 'Warning',
        text: `Please note that changing will reset all your matching criteria to match the ${configuration.configurations[0].feature_name} configuration`,
        icon: 'warning',
        confirmButtonText: 'Ok',
        cancelButtonText: 'Cancel',
        showCancelButton: true
      }).then((result) => {
        if (result.isConfirmed) {
          setConfiguration((prev) => ({
            ...prev,
            combine: !checked,
            configurations: prev.configurations.map((config) => ({
              ...config,
              configuration: {
                ...config.configuration,
                configuration: {
                  ...config.configuration.configuration,
                  matching_criteria: prev.configurations[0].configuration.configuration.matching_criteria
                }
              }
            }))
          }));
        }
      });
    } else {
      setConfiguration(() => ({
        ...configuration,
        combine: !checked
      }));
    }
  };

  // Calculate if all configurations are loaded once before using in useEffect hooks
  const allConfigsLoaded = configuration.configurations.filter((config) => !config.configuration).length === 0;

  useEffect(() => {
    setLoading(!allConfigsLoaded && !error);

    const configurations = configuration.configurations.map((config) => config.configuration)?.map((config) => config?.configuration).filter((config) => config);
    let isCombined = true;
    configurations.forEach((config, index) => {
      if (index > 0 && config.matching_criteria !== configurations[index - 1].matching_criteria) {
        isCombined = false;
      }
    });
    if (configurations.length > 0 && !isCombined) {
      setConfiguration((prev) => ({
        ...prev,
        combine: isCombined
      }));
    }

  }, [configuration.configurations, error, allConfigsLoaded]);

  useEffect(() => {
    setLoading(true);
    features.forEach((feature) => {
      getFeatureConfiguration(feature.name);
    });
  }, [employerId, features, getFeatureConfiguration]);

  return (
    <CollapsibleCard title={title} className="shadow" style={{ marginTop: '20px' }}>
      <CardBody>

        {!loading && configuration.configurations.length > 1 && (
          <Row>
            <Col lg={10}>
              Do you want to match your {title.toLowerCase()} using different identifiers?
            </Col>
            <Col lg={2}>
              <Input
                type="checkbox"
                checked={!configuration.combine}
                onChange={(e) => updateCombinedCriteria(e.target.checked)}
              />
            </Col>
          </Row>
        )}

        {loading && (
          <div className="spinner-border text-primary m-2" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {allConfigsLoaded && (
          <div>
            {configuration.configurations.map((config, index) => (
              <div key={index}>
                {!configuration.combine && <h3>{config.feature_name}</h3>}

                {index > 0 && configuration.combine ? null : (
                  <>
                    <div className="form-group">
                      {Object.keys(matchingCriteriaDictionary).map((key) => (
                        <div className="form-check" key={key}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={config.configuration.configuration.matching_criteria
                              .split(',')
                              .includes(key)}
                            value=""
                            onChange={() => toggleField(key, config.feature_name)}
                            id={`${config.configuration.id}${key}`}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`${config.configuration.id}${key}`}>
                            {matchingCriteriaDictionary[key]}
                          </label>
                        </div>
                      ))}
                    </div>
                    <div className="form-group">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="matching_criteria">Document length</label>
                      <select
                        className="form-control"
                        id="matching_criteria"
                        value={config.configuration?.configuration?.document_length || 1}
                        onChange={(e) => {
                          const value = e?.target?.value;
                          if (!value) return;

                          const newValue = parseInt(value, 10);
                          if (Number.isNaN(newValue)) return;

                          setConfiguration((prev) => ({
                            ...prev,
                            configurations: prev.configurations.map((prevConfig) =>
                              prevConfig.feature_name === config.feature_name
                                ? {
                                  ...prevConfig,
                                  configuration: {
                                    ...prevConfig.configuration,
                                    configuration: {
                                      ...prevConfig.configuration.configuration,
                                      document_length: newValue,
                                    },
                                  },
                                }
                                : prevConfig
                            ),
                          }));
                        }}
                      >
                        {Array.from(Array(5).keys()).map((key, optionIndex) => (
                          <option key={optionIndex} value={key + 1}>
                            {key + 1}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>
            ))}
            <button
              disabled={loading}
              onClick={updateMatchingCriterias}
              className="btn btn-primary btn-block">
              {loading ? 'Loading...' : 'Save Changes'}
            </button>
          </div>
        )}

      </CardBody>
    </CollapsibleCard>
  );
}
